/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import React, { useEffect } from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore,
    IEmailDelivery,
    IInvoicePaymentSummary
} from '@msdyn365-commerce-modules/checkout';
// @ts-ignore
import { toJS } from 'mobx';
declare global {
    interface Window {
        dataLayer: any[];
    }
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

// @ts-ignore
const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines, toBeCalculatedText }) => {
    // @ts-ignore
    const orderTotalPrice = lines?.orderTotal?.props?.price || 0;
    // @ts-ignore
    let customerCredit = lines?.customerAccount?.props?.price || 0;
    // @ts-ignore
    customerCredit = Math.abs(customerCredit);

    if (orderTotalPrice === 0 && lines?.customerAccount) {
        // @ts-ignore
        lines.orderTotal.props.price = customerCredit;
    }

    // @ts-ignore
    if (lines?.tax?.props?.freeText === 'Free') {
        // @ts-ignore
        lines.tax.props.freeText = toBeCalculatedText;
    }

    // @ts-ignore
    if (lines?.shipping?.props?.freeText === 'Free') {
        // @ts-ignore
        lines.shipping.props.freeText = toBeCalculatedText;
    }

    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {lines.tax}
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef
    } = props;

    const resources = props?.resources;
    const toBeCalculatedText = resources?.toBeCalculatedText;

    const products = props.data.products;
    const targetClass = 'ms-checkout__guided-card-btn-save';

    useEffect(() => {
        const isBeginCheckoutAlreadyPushed = window.dataLayer?.some(event => event.event === 'begin_checkout');
        // @ts-ignore
        const currency = props.data.customerInformation?.result?.CurrencyCode;
        // @ts-ignore
        const value = props.orderSummary?.lines?.orderTotal?.props?.price;
        // @ts-ignore
        const coupon = props.orderSummary?.lines?.totalDiscounts?.props?.id || '';
        // @ts-ignore
        const items = lineItems?.itemsForShip.lineItems?.map((lineItem, index) => {
            // @ts-ignore
            const itemData = lineItem.item?.props?.data?.product;
            const category = itemData?.ExtensionProperties?.categories;
            return {
                item_id: itemData?.RecordId?.toString(),
                item_name: itemData?.Name,
                index,
                item_category: category?.[0] || '',
                item_category2: category?.[1] || '',
                item_category3: category?.[2] || '',
                item_category4: category?.[3] || '',
                item_list_id: itemData?.ItemId || '',
                item_list_name: category?.[0] || '',
                price: itemData?.Price,
                quantity: 1
            };
        });

        if (!isBeginCheckoutAlreadyPushed && lineItems && products?.result && items) {
            window.dataLayer = window?.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'begin_checkout',
                ecommerce: {
                    currency,
                    value,
                    coupon,
                    items
                }
            });
        }

        const observer = new MutationObserver((mutations, obs) => {
            const saveButton = document.querySelector(`.${targetClass}`);
            const payButton = document.querySelector('.ms-checkout-customer-account__btn-pay');

            if (saveButton && payButton) {
                saveButton.addEventListener('click', () => {
                    const addressSelect = document.querySelector('.msc-address-select');
                    const deliveryOptions = document.querySelector('.ms-checkout-delivery-options');

                    // @ts-ignore
                    const isAddressVisible = addressSelect && addressSelect?.offsetParent !== null;
                    // @ts-ignore
                    const isDeliveryVisible = deliveryOptions && deliveryOptions?.offsetParent !== null;

                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ ecommerce: null });

                    const isBeginAddressAlreadyPushed = window.dataLayer?.some(event => event.event === 'add_shipping_info');

                    if (isAddressVisible && !isBeginAddressAlreadyPushed) {
                        window.dataLayer.push({
                            event: 'add_shipping_info',
                            ecommerce: {
                                currency,
                                value,
                                coupon,
                                items
                            }
                        });
                        console.log('Shipping info sent');
                    }
                });

                payButton.addEventListener('click', () => {
                    const isBeginPaymentAlreadyPushed = window.dataLayer?.some(event => event.event === 'add_payment_info');

                    if (!isBeginPaymentAlreadyPushed) {
                        window.dataLayer.push({
                            event: 'add_payment_info',
                            ecommerce: {
                                currency,
                                value,
                                coupon,
                                payment_type: 'account credit',
                                items
                            }
                        });
                        console.log('Payment info sent');
                    }
                });

                obs.disconnect();

                function logClickAndTriggerNext() {
                    const selectors = [
                        '.ms-checkout__guided-card-footer',
                        '.ms-checkout__guided-card-btn-save'
                    ];
                
                    const clickIfExists = (selector: string) => {
                        const element = document.querySelector(selector) as HTMLElement;
                        if (element) element.click();
                    };
                
                    const observer = new MutationObserver(() => {
                        const cards = document.querySelectorAll('.ms-checkout__guided-card');
                    
                        cards.forEach((card) => {
                            const applyButton = card.querySelector('.ms-checkout-customer-account__btn-pay') as HTMLElement;
                            const appliedLine = card.querySelector('.ms-checkout-customer-account__applied-line') as HTMLElement;
                            const saveButton = card.querySelector('.ms-checkout__guided-card-btn-save') as HTMLButtonElement;
                    
                            if (saveButton && !saveButton.dataset.blocked && !appliedLine && applyButton) {
                                saveButton.disabled = true;
                                saveButton.addEventListener('click', (e) => e.preventDefault());
                                saveButton.dataset.blocked = 'true';
                            }
                    
                            if (saveButton && appliedLine) {
                                saveButton.disabled = false;
                            }
                    
                            if (applyButton && !applyButton.dataset.listenerAdded) {
                                applyButton.addEventListener('click', () => {
                                    setTimeout(() => selectors.forEach(clickIfExists), 500);
                                });
                                applyButton.dataset.listenerAdded = 'true';
                            }
                        });
                    });
                    
                    observer.observe(document.body, { childList: true, subtree: true });
                }
                
                logClickAndTriggerNext();
                
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    }, [lineItems, products]);

    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && (
                                          <OrderSummaryComponent
                                              {...orderSummary}
                                              // @ts-ignore
                                              toBeCalculatedText={toBeCalculatedText}
                                          />
                                      )
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItems} />}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
